/**
 * Shared geometry for the radial 24-hour day clock — the SINGLE source of truth
 * for the time → angle mapping, so every layer (hour dial, block wedges, the
 * solar/prayer rings, the sleep wedge, the now-hand) stays in lock-step.
 *
 * Orientation: NOON at the top, midnight at the bottom, time running CLOCKWISE
 * (so 6am is on the LEFT and 6pm on the RIGHT). SVG angles are measured the SVG
 * way: 0° points right (east) and positive angles sweep clockwise (downward).
 */

export const CLOCK_CENTER = 50;

/**
 * Minute-of-day [0,1440) → SVG angle in degrees.
 * `minute / 4` spreads 1440 minutes across 360°; the −270 offset places noon
 * (minute 720) at −90° (the top) with midnight at the bottom.
 */
export const minuteToAngleDeg = (minute: number): number => minute / 4 - 270;

/** Hour-of-day [0,24) → SVG angle in degrees (noon at top). */
export const hourToAngleDeg = (hour: number): number => minuteToAngleDeg(hour * 60);

/** Minute-of-day → SVG angle in radians. */
export const minuteToAngleRad = (minute: number): number => (minuteToAngleDeg(minute) * Math.PI) / 180;

/** Parse "HH:MM" → minutes since midnight. */
export const hhmmToMinutes = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

/** Point [x,y] on a ring of radius `r` for a minute-of-day, around `center`. */
export function pointAtMinute(minute: number, r: number, center = CLOCK_CENTER): [number, number] {
  const a = minuteToAngleRad(minute);
  return [center + r * Math.cos(a), center + r * Math.sin(a)];
}

/**
 * Point on a ring for a normalized fraction [0,1) measured CLOCKWISE FROM THE TOP
 * (12 o'clock). Used by the routine-timer arc, which is progress-based rather than
 * tied to wall-clock minutes. SVG y is down, so a positive angle sweeps clockwise.
 */
export function pointAtFraction(fraction: number, r: number, center = CLOCK_CENTER): [number, number] {
  const a = ((-90 + fraction * 360) * Math.PI) / 180;
  return [center + r * Math.cos(a), center + r * Math.sin(a)];
}

/**
 * SVG path `d` for a stroked arc from `fromFrac` to `toFrac` (clockwise from top).
 * For a near-full ring use a <circle> instead — a path whose start and end points
 * coincide won't render.
 */
export function arcPath(fromFrac: number, toFrac: number, r: number, center = CLOCK_CENTER): string {
  const [x0, y0] = pointAtFraction(fromFrac, r, center);
  const [x1, y1] = pointAtFraction(toFrac, r, center);
  const largeArc = toFrac - fromFrac > 0.5 ? 1 : 0;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1}`;
}

/**
 * Start/end SVG angles (degrees) for a time span, with the end unwrapped past
 * the start so a span crossing the bottom (midnight) still sweeps forward.
 */
export function spanAnglesDeg(startTime: string, endTime: string): { startAngle: number; endAngle: number } {
  const startAngle = minuteToAngleDeg(hhmmToMinutes(startTime));
  let endAngle = minuteToAngleDeg(hhmmToMinutes(endTime));
  if (endAngle <= startAngle) endAngle += 360;
  return { startAngle, endAngle };
}
